import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageBreadcrumb from "../components/common/PageBreadCrumb";
import { getCurrentAgentProfile, updateAgentProfile } from "../services/agent_management_service";
import { resetAgentPassword } from "../services/authService";
import { setUser, logout } from "../store/slicers/authSlicer";
import { useModal } from "../hooks/useModal";
import { Modal } from "../components/ui/modal";
import Input from "../components/form/input/InputField";
import Label from "../components/form/Label";
import Button from "../components/ui/button/Button";
import { getCoutries, getProvincesBaseOnCountry, getDistrictsBaseOnProvince } from "../services/public_service";
import { useTranslation } from "react-i18next";

const UserProfiles = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.auth.user);
  

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  

  const { isOpen, openModal, closeModal } = useModal();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNumber: "",
    alternativeContact: "",
    messageLanguage: "english",
    profile_picture: null,
    country: "",
    province: "",
    district: "",
    address: ""
  });
  
  const [previewImage, setPreviewImage] = useState(null);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL;


  useEffect(() => {
    if (userInfo) {
      setFormData({
        username: userInfo?.username || "",
        email: userInfo?.email || "",
        mobileNumber: userInfo?.mobileNumber || "",
        alternativeContact: userInfo?.agentDetail?.alternativeContact || "",
        messageLanguage: userInfo?.agentDetail?.messageLanguage || "english",
        profile_picture: null,
        country: userInfo?.agentDetail?.country || "",
        province: userInfo?.agentDetail?.province || "",
        district: userInfo?.agentDetail?.district || "",
        address: userInfo?.agentDetail?.address || ""
      });
      
      if (userInfo?.profile_picture) {
        setPreviewImage(`${IMG_BASE_URL}${userInfo.profile_picture}`);
      }
    }
  }, [userInfo, IMG_BASE_URL]);


  useEffect(() => {
    if (isOpen) {
      loadCountries();
    }
  }, [isOpen]);


  useEffect(() => {
    if (formData.country) {
      loadProvinces(formData.country);
    } else {
      setProvinces([]);
      setDistricts([]);
    }
  }, [formData.country]);


  useEffect(() => {
    if (formData.province) {
      loadDistricts(formData.province);
    } else {
      setDistricts([]);
    }
  }, [formData.province]);


  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await getCoutries();
      console.log("Countries API Response:", response);
      
      if (response.status === true || response.success) {
        const countriesData = response.data || response.data?.data || [];
        setCountries(countriesData);
        console.log("Loaded countries:", countriesData.length);
      } else {
        console.error("Countries API returned false status:", response);
        showSnackbar(t('profile.errors.loadCountries'), "error");
      }
    } catch (error) {
      console.error("Error loading countries:", error);
      showSnackbar(t('profile.errors.loadCountries'), "error");
    } finally {
      setLoadingCountries(false);
    }
  };


  const loadProvinces = async (countryId) => {
    if (!countryId) {
      setProvinces([]);
      return;
    }

    setLoadingProvinces(true);
    try {
      console.log("Loading provinces for country:", countryId);
      const response = await getProvincesBaseOnCountry(countryId);
      console.log("Provinces API Response:", response);
      
      if (response.status === true || response.success) {
        let provincesData = [];
        
        if (Array.isArray(response.data)) {
          provincesData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          provincesData = response.data.data;
        } else if (Array.isArray(response.data)) {
          provincesData = response.data;
        }
        
        console.log("Processed provinces data:", provincesData);
        setProvinces(provincesData);
        
        if (provincesData.length === 0) {
          console.warn("No provinces found for country:", countryId);
        }
      } else {
        console.error("Provinces API returned false status:", response);
        showSnackbar(t('profile.errors.loadProvinces'), "error");
      }
    } catch (error) {
      console.error("Error loading provinces:", error);
      showSnackbar(t('profile.errors.loadProvinces'), "error");
    } finally {
      setLoadingProvinces(false);
    }
  };

  const loadDistricts = async (provinceId) => {
    if (!provinceId) {
      setDistricts([]);
      return;
    }

    setLoadingDistricts(true);
    try {
      console.log("Loading districts for province:", provinceId);
      const response = await getDistrictsBaseOnProvince(provinceId);
      console.log("Districts API Response:", response);
      
      if (response.status === true || response.success) {
        let districtsData = [];
        
        if (Array.isArray(response.data)) {
          districtsData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          districtsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          districtsData = response.data;
        }
        
        console.log("Processed districts data:", districtsData);
        setDistricts(districtsData);
        
        if (districtsData.length === 0) {
          console.warn("No districts found for province:", provinceId);
        }
      } else {
        console.error("Districts API returned false status:", response);
        showSnackbar(t('profile.errors.loadDistricts'), "error");
      }
    } catch (error) {
      console.error("Error loading districts:", error);
      showSnackbar(t('profile.errors.loadDistricts'), "error");
    } finally {
      setLoadingDistricts(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleResetPassword = async () => {
    if (!passwordForm.currentPassword) {
      showSnackbar(t('profile.errors.currentPasswordRequired'), "error");
      return;
    }
    
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      showSnackbar(t('profile.errors.newPasswordRequired'), "error");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showSnackbar(t('profile.errors.passwordMismatch'), "error");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      showSnackbar(t('profile.errors.passwordMinLength'), "error");
      return;
    }

    setResetPasswordLoading(true);
    try {
      const response = await resetAgentPassword(passwordForm);
      
      if (response.status === true) {
        showSnackbar(response.message || t('profile.success.passwordReset'), "success");
        
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        
        setResetPasswordModalOpen(false);
        
        if (response.logoutRequired) {
          setTimeout(() => {
            dispatch(logout());
            navigate("/login");
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Reset password error:", error);
      showSnackbar(
        error.response?.data?.error || t('profile.errors.passwordResetFailed'),
        "error"
      );
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleUpdateProfile = async (updateData) => {
    setLoading(true);
    try {
      const response = await updateAgentProfile(updateData);
      
      if (response.status === "success") {
        dispatch(setUser(response.data.user));
        
        const profileResponse = await getCurrentAgentProfile();
        console.log(profileResponse, "this is profile response");
        if (profileResponse) {
          dispatch(setUser({
            ...userInfo,
            ...profileResponse.user,
            agentDetail: profileResponse
          }));
        }
        
        showSnackbar(t('profile.success.profileUpdated'));
        closeModal();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showSnackbar(t('profile.errors.updateFailed'), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.username || !formData.email) {
        showSnackbar(t('profile.errors.requiredFields'), "error");
        return;
      }

      const submitData = {
        ...formData,
        country: formData.country ? parseInt(formData.country) : null,
        province: formData.province ? parseInt(formData.province) : null,
        district: formData.district ? parseInt(formData.district) : null
      };
      
      await handleUpdateProfile(submitData);
    } catch (error) {
      console.error("Error saving profile:", error);
    }
  };


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profilePicture: file
      }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'country') {
      setFormData(prev => ({
        ...prev,
        province: "",
        district: ""
      }));
      setProvinces([]);
      setDistricts([]);
    } else if (field === 'province') {
      setFormData(prev => ({
        ...prev,
        district: ""
      }));
      setDistricts([]);
    }
  };

  const getDisplayName = (field, details) => {
    if (!details) return t('profile.notProvided');
    const nameObj = details[field] || {};
    return nameObj.en || nameObj.fa || nameObj.dr || t('profile.notProvided');
  };

  const getOptionDisplayName = (item, nameField = "countryName") => {
    if (!item) return "Unknown";
    
    const nameObj = item[nameField];
    if (typeof nameObj === 'object') {
      return nameObj.en || nameObj.fa || nameObj.dr || nameObj;
    }
    return nameObj || item.name || "Unknown";
  };

  const getSelectedOption = (options, value, idField = "id") => {
    return options.find(opt => opt[idField] == value) || null;
  };

  return (
    <div className="p-8">
      <PageBreadcrumb pageTitle={t('profile.pageTitle')} />
      
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">
          {t('profile.title')}
        </h3>
        
        <div className="space-y-6">
          <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
                <div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
                  <img 
                    src={previewImage || (userInfo?.profilePicture ? `${IMG_BASE_URL}${userInfo.profilePicture}` : "/default-avatar.png")} 
                    alt="user" 
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="order-3 xl:order-2">
                  <h4 className="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                    {userInfo?.username}
                  </h4>
                  <div className="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {userInfo?.agentDetail?.address || t('profile.notProvided')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="w-[15%]">
                <button
                  onClick={openModal}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 lg:inline-flex lg:w-auto"
                  disabled={loading}
                >
                  <svg
                    className="fill-current"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.0911 2.78206C14.2125 1.90338 12.7878 1.90338 11.9092 2.78206L4.57524 10.116C4.26682 10.4244 4.0547 10.8158 3.96468 11.2426L3.31231 14.3352C3.25997 14.5833 3.33653 14.841 3.51583 15.0203C3.69512 15.1996 3.95286 15.2761 4.20096 15.2238L7.29355 14.5714C7.72031 14.4814 8.11172 14.2693 8.42013 13.9609L15.7541 6.62695C16.6327 5.74827 16.6327 4.32365 15.7541 3.44497L15.0911 2.78206ZM12.9698 3.84272C13.2627 3.54982 13.7376 3.54982 14.0305 3.84272L14.6934 4.50563C14.9863 4.79852 14.9863 5.2734 14.6934 5.56629L14.044 6.21573L12.3204 4.49215L12.9698 3.84272ZM11.2597 5.55281L5.6359 11.1766C5.53309 11.2794 5.46238 11.4099 5.43238 11.5522L5.01758 13.5185L6.98394 13.1037C7.1262 13.0737 7.25666 13.003 7.35947 12.9002L12.9833 7.27639L11.2597 5.55281Z"
                      fill=""
                    />
                  </svg>
                  {loading ? t('profile.updating') : t('profile.editProfile')}
                </button>
              </div>
            </div>
          </div>

          <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
                  {t('profile.personalInfo')}
                </h4>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.username')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.username}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.email')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.email}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.mobileNumber')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.mobileNumber || t('profile.notProvided')}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.alternativeContact')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.agentDetail?.alternativeContact || t('profile.notProvided')}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.messageLanguage')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90 capitalize">
                      {userInfo?.agentDetail?.messageLanguage}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
                  {t('profile.address')}
                </h4>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.country')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {getDisplayName("countryName", userInfo?.agentDetail?.countryDetails)}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.province')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {getDisplayName("provinceName", userInfo?.agentDetail?.provinceDetails)}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.district')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {getDisplayName("districtName", userInfo?.agentDetail?.districtDetails)}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.addressLabel')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.agentDetail?.address || t('profile.notProvided')}
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                      {t('profile.id')}
                    </p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {userInfo?.id}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
            <h4 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
              {t('profile.security')}
            </h4>
            
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  {t('profile.accountStatus')}
                </p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  userInfo?.status === "active" 
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" 
                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                }`}>
                  {userInfo?.status === "active" ? t('profile.active') : t('profile.inactive')}
                </span>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  {t('profile.lastLogin')}
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {userInfo?.lastLogin ? new Date(userInfo.lastLogin).toLocaleString() : t('profile.never')}
                </p>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => setResetPasswordModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-3 text-sm font-medium text-red-700 shadow-theme-xs hover:bg-red-50 hover:text-red-800 dark:border-red-700 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                {t('profile.resetPassword')}
              </button>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('profile.resetPasswordNote')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[800px] m-4">
        <div className="no-scrollbar relative w-full max-w-[800px] overflow-y-auto rounded-3xl bg-white p-4 dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              {t('profile.editProfileTitle')}
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              {t('profile.editProfileDescription')}
            </p>
          </div>
          <form className="flex flex-col" onSubmit={(e) => e.preventDefault()}>
            <div className="custom-scrollbar max-h-[600px] overflow-y-auto px-2 pb-3">
              <div className="flex flex-col items-center mb-6">
                <div className="relative">
                  <img 
                    src={previewImage || (userInfo?.profile_picture ? `${IMG_BASE_URL}${userInfo.profile_picture}` : "/default-avatar.png")} 
                    alt="Profile" 
                    className="w-32 h-32 rounded-full object-cover border-2 border-gray-300"
                  />
                  <label 
                    htmlFor="profile-upload"
                    className="absolute bottom-0 right-0 bg-blue-500 text-white p-2 rounded-full cursor-pointer hover:bg-blue-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </label>
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <h5 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90 border-b pb-2">
                  {t('profile.personalInfo')}
                </h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div className="col-span-2 lg:col-span-1">
                    <Label>{t('profile.username')} *</Label>
                    <Input 
                      type="text" 
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-span-2 lg:col-span-1">
                    <Label>{t('profile.email')} *</Label>
                    <Input 
                      type="email" 
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-span-2 lg:col-span-1">
                    <Label>{t('profile.mobileNumber')}</Label>
                    <Input 
                      type="text" 
                      value={formData.mobileNumber}
                      onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                    />
                  </div>

                  <div className="col-span-2 lg:col-span-1">
                    <Label>{t('profile.alternativeContact')}</Label>
                    <Input 
                      type="text" 
                      value={formData.alternativeContact}
                      onChange={(e) => handleInputChange('alternativeContact', e.target.value)}
                    />
                  </div>

                  <div className="col-span-2">
                    <Label>{t('profile.messageLanguage')} *</Label>
                    <select
                      value={formData.messageLanguage}
                      onChange={(e) => handleInputChange('messageLanguage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    >
                      <option value="english">{t('profile.languages.english')}</option>
                      <option value="dari">{t('profile.languages.dari')}</option>
                      <option value="pashto">{t('profile.languages.pashto')}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h5 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90 border-b pb-2">
                  {t('profile.addressInfo')}
                </h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div>
                    <Label>{t('profile.country')} *</Label>
                    <select
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      disabled={loadingCountries}
                    >
                      <option value="">{t('profile.selectCountry')}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {getOptionDisplayName(country, "countryName")}
                        </option>
                      ))}
                    </select>
                    {loadingCountries && <p className="text-sm text-gray-500 mt-1">{t('profile.loadingCountries')}</p>}
                  </div>

                  <div>
                    <Label>{t('profile.province')} *</Label>
                    <select
                      value={formData.province}
                      onChange={(e) => handleInputChange('province', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      disabled={!formData.country || loadingProvinces}
                    >
                      <option value="">{t('profile.selectProvince')}</option>
                      {provinces.map((province) => (
                        <option key={province.id} value={province.id}>
                          {getOptionDisplayName(province, "provinceName")}
                        </option>
                      ))}
                    </select>
                    {loadingProvinces && <p className="text-sm text-gray-500 mt-1">{t('profile.loadingProvinces')}</p>}
                    {!loadingProvinces && formData.country && provinces.length === 0 && (
                      <p className="text-sm text-yellow-600 mt-1">{t('profile.noProvinces')}</p>
                    )}
                  </div>

                  <div>
                    <Label>{t('profile.district')} *</Label>
                    <select
                      value={formData.district}
                      onChange={(e) => handleInputChange('district', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      disabled={!formData.province || loadingDistricts}
                    >
                      <option value="">{t('profile.selectDistrict')}</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {getOptionDisplayName(district, "districtName")}
                        </option>
                      ))}
                    </select>
                    {loadingDistricts && <p className="text-sm text-gray-500 mt-1">{t('profile.loadingDistricts')}</p>}
                    {!loadingDistricts && formData.province && districts.length === 0 && (
                      <p className="text-sm text-yellow-600 mt-1">{t('profile.noDistricts')}</p>
                    )}
                  </div>

                  <div>
                    <Label>{t('profile.addressLabel')}</Label>
                    <Input 
                      type="text" 
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder={t('profile.addressPlaceholder')}
                    />
                  </div>

                  <div>
                    <Label>{t('profile.id')}</Label>
                    <Input 
                      type="text" 
                      value={userInfo?.id || ""}
                      disabled
                      className="bg-gray-100 dark:bg-gray-800"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-2 mt-6 lg:justify-end">
              <Button size="sm" variant="outline" onClick={closeModal} disabled={loading}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={loading}>
                {loading ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal 
        isOpen={resetPasswordModalOpen} 
        onClose={() => {
          setResetPasswordModalOpen(false);
          setPasswordForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        }} 
        className="max-w-[500px] m-4"
      >
        <div className="no-scrollbar relative w-full max-w-[500px] overflow-y-auto rounded-3xl bg-white p-4 dark:bg-gray-900 lg:p-8">
          <div className="px-2 pr-8">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              {t('profile.resetPassword')}
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              {t('profile.resetPasswordDescription')}
            </p>
          </div>
          
          <form className="flex flex-col" onSubmit={(e) => e.preventDefault()}>
            <div className="px-2 pb-3">
              <div className="mb-4">
                <Label>{t('profile.currentPassword')} *</Label>
                <Input 
                  type="password" 
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({
                    ...prev,
                    currentPassword: e.target.value
                  }))}
                  placeholder={t('profile.currentPasswordPlaceholder')}
                  required
                />
              </div>
              
              <div className="mb-4">
                <Label>{t('profile.newPassword')} *</Label>
                <Input 
                  type="password" 
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({
                    ...prev,
                    newPassword: e.target.value
                  }))}
                  placeholder={t('profile.newPasswordPlaceholder')}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.passwordRequirements')}
                </p>
              </div>
              
              <div className="mb-6">
                <Label>{t('profile.confirmPassword')} *</Label>
                <Input 
                  type="password" 
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }))}
                  placeholder={t('profile.confirmPasswordPlaceholder')}
                  required
                />
              </div>
              
              <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-lg dark:bg-yellow-900/30 dark:text-yellow-300">
                <svg className="inline w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                </svg>
                {t('profile.resetPasswordWarning')}
              </div>
            </div>
            
            <div className="flex items-center gap-3 px-2 mt-4 lg:justify-end">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => {
                  setResetPasswordModalOpen(false);
                  setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                disabled={resetPasswordLoading}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                size="sm" 
                onClick={handleResetPassword}
                disabled={resetPasswordLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {resetPasswordLoading ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('profile.resetting')}
                  </>
                ) : t('profile.resetPassword')}
              </Button>
            </div>
          </form>
        </div>
      </Modal>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserProfiles;